import { useEffect, useState } from 'react';

import commonGlobals from '@opendoor/shared-fe/commonGlobals';
import { getRailsEnv } from '@opendoor/shared-fe/railsEnv';
import { SystemError } from 'platform/Error';

/**
 * Calculates the environment based on the window object or the process.env.DEPLOY_ENV depending on whether or not the code is running on the server or client.
 */
const getDeployEnv = () => {
  if (typeof window === 'undefined') {
    // eslint-disable-next-line n/no-process-env, @opendoor/no-direct-env-check -- only called on server
    return process.env.DEPLOY_ENV as keyof typeof commonGlobals;
  }
  // eslint-disable-next-line @opendoor/no-direct-env-check -- checking for client-side deploy env
  if (window.ENV?.DEPLOY_ENV) {
    // eslint-disable-next-line @opendoor/no-direct-env-check -- only called on client
    return window.ENV?.DEPLOY_ENV as keyof typeof commonGlobals;
  }

  // Fallsback to Rails env method in shared-fe
  return getRailsEnv();
};
const env = getDeployEnv();

const secrets = commonGlobals[env] ?? {};

export const WEB_API_URL = secrets.webApiUrl;
const useStaging = WEB_API_URL?.includes('staging');
export const RAILS_ENV = useStaging ? 'staging' : env;

export const GOOGLE_CLIENT_ID = secrets.googleClientID;
export const GOOGLE_MAPS_KEY = secrets.googleMaps;
export const RECEPTION_FE_URL = secrets.consumerFeUrl;
export const ADMIN_CLIENT_UPLOAD_BUCKET = `opendoor-admin-client-upload-${
  RAILS_ENV === 'development' ? 'staging' : RAILS_ENV
}`;

export const CXA_EMAIL = 'contact@opendoor.com';
export const CXA_EMAIL_NEW = 'hello@opendoor.com';
export const PRE_UNDERWRITING_CXA_EMAIL = 'support@opendoor.com';

export const CONTACT_PHONE = '888-352-7075';

export const offerPollingTimeoutThreshold =
  // eslint-disable-next-line no-nested-ternary -- Grandfathered violation
  RAILS_ENV === 'test' ? 10_000 : RAILS_ENV === 'production' ? 90_600 : 120_000;
export const backendRefreshBlockDurationMS = 120_000;

export const DOMAIN = (() => {
  // Auto-detect PR preview domains and use current origin for auth redirects
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.match(/^pr-\d+-[\w.-]*\.apps-staging\.opendoor\.com$/)) {
      return window.location.origin;
    }
  }
  return {
    production: 'https://consumer.opendoor.com',
    staging: 'https://consumer.simplersell.com',
    qa: 'https://consumer.simplersell.com',
    development: 'http://localhost:3016',
    test: 'https://demo.simplersell.com',
  }[RAILS_ENV];
})();
export const COSMOS_DOMAIN = {
  production: 'https://www.opendoor.com',
  staging: 'https://demo.simplersell.com',
  qa: 'https://demo.simplersell.com',
  development: 'http://localhost:3011',
  test: 'https://demo.simplersell.com',
}[RAILS_ENV];
export const HOMEPAGE_URL = {
  production: 'https://www.opendoor.com',
  staging: 'https://demo.simplersell.com',
  qa: 'https://demo.simplersell.com',
  development: 'http://localhost:3011/homepage-2019',
  test: 'https://demo.simplersell.com',
}[RAILS_ENV];

// SSVA confirmation
export const PRODUCTION_SELLER_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSe4glJ1pVRnUZdnpHbMDywnF1t5FAnhyQrQ7Q338WGmuelQRg/viewform?usp=pp_url';
export const STAGING_SELLER_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSe64KWEV4fKnLlwTAAi8PtoV09SOXtCfHAYviEJK4PrjY8KEA/viewform?usp=pp_url';
export const PRODUCTION_ALA_EXPERIENCE_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSeQHDLKdpDmmwpcr5r5JARETAIFxICUth4UFsWFRFV-5uY8Dw/viewform?usp=pp_url';
export const STAGING_ALA_EXPERIENCE_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSff6cvFZr4iSmazrSORUD7d04RADQG-lVzbhqP3ZttCP_k_TA/viewform?usp=pp_url';
export const FLIP_TOKEN_FORM_ENTRY = 'entry.1387189579';
export const EMAIL_FORM_ENTRY = 'entry.1100801854';
export const NAME_FORM_ENTRY = 'entry.678167604';

// Public token; meant to be exposed client-side.
// URL restrictions are configured in the Mapbox admin panel.
export const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1Ijoib3BlbmRvb3IiLCJhIjoiY21tbndldHRzMDdsaTJwcHcwOWx0YnByZSJ9.TIy40uVqEjM_ZQxuGnFd7g';
export const MAPBOX_MAP_STYLE = 'mapbox://styles/opendoor/cktkqasmc1gbe17pfo2cm7ybb';

export const HELLOSIGN_CLIENT_ID = secrets.hellosignClientID;

export const useBrowserCompatibilityData = () => {
  const [userAgent, setUserAgent] = useState<string>('');
  const [supportsNativeSmoothScroll, setSupportsNativeSmoothScroll] = useState<boolean>(false);

  // Sets variables once DOM has mounted to avoid SSR issues
  useEffect(() => {
    setUserAgent(window.navigator.userAgent ?? '');
    setSupportsNativeSmoothScroll('scrollBehavior' in document.documentElement.style);
  }, []);

  const isOpendoorAppEvoWebView = userAgent.indexOf('EvolvedWebView') > -1;
  const isOpendoorAppWebView =
    userAgent.indexOf('OpendoorAppWebView') > -1 && !isOpendoorAppEvoWebView;
  const isWebView = isOpendoorAppWebView || isOpendoorAppEvoWebView;
  return {
    userAgent,
    /** True if the user is using chrome */
    IS_USING_CHROME: !!userAgent.includes('Chrome'),
    /**
     * Updated User Agent that OpendoorAppWebView/EvolvedWebView/${DeviceVersion}
     * This will not break any existing webview checks
     */
    isOpendoorAppEvoWebView: !!isOpendoorAppEvoWebView,
    isOpendoorAppWebView: !!isOpendoorAppWebView,
    isWebView: !!isWebView,
    /**
     * Whether the browser supports smooth scrolling on elements
     * See more here: https://caniuse.com/?search=scrollTo
     */
    supportsNativeSmoothScroll: !!supportsNativeSmoothScroll,
  };
};

/**
 * Checks that a value is not null nor undefined.
 *
 * Can be used for filtering null and undefined values.
 * ```ts
 * const x: (number | null)[]
 * const y: number[] = x.filter(hasValue)
 * ```
 */
export const hasValue = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

type NonNullableTuple<T extends readonly unknown[]> = {
  [K in keyof T]: NonNullable<T[K]>;
};

/**
 * If all values are present (non-null, non-undefined), applies the callback
 * and returns its result. Otherwise returns the default value.
 *
 * ```ts
 * const a: string | null;
 * const b: number | undefined;
 *
 * // Without callback — returns narrowed tuple or false
 * const checked = mapPresentValues([a, b]);
 * if (checked) { const [x, y] = checked; } // x: string, y: number
 *
 * // With callback and default — never returns false
 * const safe = mapPresentValues([a, b], (x, y) => x.length + y, 0);
 * // safe: number
 * ```
 */
export function mapPresentValues<const T extends readonly unknown[]>(
  values: readonly [...T],
): NonNullableTuple<T> | false;
export function mapPresentValues<const T extends readonly unknown[], R>(
  values: readonly [...T],
  fn: (...args: NonNullableTuple<T>) => R,
  defaultValue: R,
): R;
export function mapPresentValues<const T extends readonly unknown[], R>(
  values: readonly [...T],
  fn?: (...args: NonNullableTuple<T>) => R,
  defaultValue?: R,
): NonNullableTuple<T> | R | false {
  if (values.some((v) => v === null || v === undefined)) {
    return defaultValue !== undefined ? defaultValue : false;
  }
  const narrowed = values as unknown as NonNullableTuple<T>;
  return fn ? fn(...narrowed) : narrowed;
}

/**
 * Returns true if all of the provided values are not null nor undefined.
 */
export const allHaveValue = (...values: unknown[]): boolean => {
  return values.every((v) => v !== null && v !== undefined);
};

/**
 * Asserts that a value is not null nor undefined.
 * @throws Error if null or undefined
 */
export const assertHasValue = <T>(name: string, value: T | null | undefined): T => {
  if (hasValue(value)) {
    return value;
  }

  throw new SystemError(`expected ${name} not to be empty, but was ${value}`);
};

type Entry<T> = { [K in keyof T]: [K, T[K]] }[keyof T];

/**
 * Type-safe version of Object.entries that preserves key types
 * @public
 */
export const typedEntries = Object.entries as <T extends object>(obj: T) => Entry<T>[];

/**
 * Type-safe version of Object.keys that preserves key types
 * @public
 */
export const typedKeys = <T extends object>(obj: T): (keyof T)[] => Object.keys(obj) as (keyof T)[];

/**
 * Type-safe version of Object.values that preserves value types
 * @public
 */
export const typedValues = <T extends object>(obj: T): T[keyof T][] =>
  Object.values(obj) as T[keyof T][];
